import { useEffect, useMemo, useState, useRef } from 'react';
import mammoth from 'mammoth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  FileText, Upload, Trash2, Download, Search, Copy, Check,
  FileSignature, ShieldCheck, AlertTriangle, Eye, Sparkles, X, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { docxTemplatesApi } from '../lib/api';

// Placeholder catalog raggruppato (sync con TemplateVariablesPage)
const PLACEHOLDER_GROUPS = [
  {
    title: 'Dipendente',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    items: [
      ['Nome_corsista', 'Nome dipendente'],
      ['Cognome_corsista', 'Cognome dipendente'],
      ['Nome_Designato', 'Nome (template Designazione)'],
      ['Cognome_Designato', 'Cognome (template Designazione)'],
      ['Cod_Fiscale', 'Codice Fiscale'],
      ['Corsista_il', 'Data di nascita'],
      ['Corsista_Nato', 'Luogo di nascita'],
      ['Mansione_unilav', 'Mansione (unilav)'],
      ['Mansione', 'Mansione'],
      ['Indirizzo_Residenza', 'Indirizzo residenza'],
      ['Citta_Residenza', 'Città residenza'],
      ['Email_Dipendente', 'Email'],
    ],
  },
  {
    title: 'Azienda / UL',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    items: [
      ['Azianda_organizzatrice', 'Ragione sociale'],
      ['Sede_Azienda', 'Indirizzo sede UL'],
      ['Luogo_Sede', 'Città UL'],
      ['Luogo_operativa', 'Città UL (alias Designazione)'],
      ['Cap', 'CAP'],
      ['Provincia', 'Provincia'],
      ['PIVA', 'Partita IVA'],
      ['Email_Aziendale', 'Email azienda'],
      ['Telefono_Aziendale', 'Telefono azienda'],
    ],
  },
  {
    title: 'Datore di Lavoro',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    items: [
      ['Nome_Datore_Lavoro', 'Nome datore'],
      ['Datore', 'Alias datore'],
      ['Legale_Rappresentante', 'Legale rappresentante'],
    ],
  },
  {
    title: 'Professionista (firmatario)',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    items: [
      ['nome_prof.', 'Nome professionista'],
      ['cognome_prof.', 'Cognome professionista'],
      ['titolo_prof.', 'Titolo (Ing., Dott.)'],
      ['Titolo_Nome_RSPP', 'Titolo + Nome completo'],
      ['Firma_RSPP', 'Firma autografa (immagine)'],
    ],
  },
  {
    title: 'Date',
    color: 'bg-rose-100 text-rose-800 border-rose-200',
    items: [
      ['Data_Odierna', 'Data del documento'],
      ['Data_incarico', 'Data incarico'],
      ['Data_Designazione', 'Data designazione'],
      ['Data_consegna_DPI', 'Data consegna DPI'],
      ['Data_corso', 'Data corso'],
    ],
  },
  {
    title: 'DPI',
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    items: [
      ['DPI_ROW', 'Marker riga tabella DPI (clonata)'],
      ['DPI_Nome', 'Nome DPI'],
      ['DPI_Numero', 'N. progressivo'],
      ['DPI_Lista', 'Lista DPI concatenati'],
    ],
  },
];

const ALL_PLACEHOLDERS = PLACEHOLDER_GROUPS.flatMap((g) => g.items.map(([k]) => k));

const SAMPLE_TEMPLATES = [
  { code: 'incarico_rspp', label: 'Incarico RSPP', icon: FileSignature },
  { code: 'art18_dpi', label: 'Art. 18 — Consegna DPI', icon: ShieldCheck },
  { code: 'designazione_aa_ps', label: 'Designazione AA / PS', icon: FileText },
];

export default function DocxTemplatesManagerPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState('sicurezza');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Preview dialog
  const [previewTpl, setPreviewTpl] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [validateResult, setValidateResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState('');
  const previewRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await docxTemplatesApi.getAll();
      setTemplates(r.data || []);
    } catch {
      toast.error('Errore caricamento template');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates
      .filter((t) => filterCat === 'all' || t.category === filterCat)
      .filter((t) => {
        if (!q) return true;
        return (
          (t.name || '').toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q) ||
          (t.original_filename || '').toLowerCase().includes(q)
        );
      });
  }, [templates, search, filterCat]);

  const downloadSample = async (code) => {
    try {
      const r = await api.get(`/docx-templates/sample/${code}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `Esempio_${code}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template di esempio scaricato');
    } catch {
      toast.error('Errore download esempio');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Seleziona un file .docx'); return; }
    if (!uploadName) { toast.error('Inserisci un nome'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      await api.post('/docx-templates/upload', fd, {
        params: { name: uploadName, category: uploadCategory, description: uploadDescription },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Template caricato');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadName('');
      setUploadDescription('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Eliminare il template "${tpl.name}"?`)) return;
    try {
      await docxTemplatesApi.delete(tpl.id);
      toast.success('Template eliminato');
      await load();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const handleDownload = async (tpl) => {
    try {
      const r = await api.get(`/docx-templates/${tpl.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = tpl.original_filename || `${tpl.name}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Errore download');
    }
  };

  const openPreview = async (tpl) => {
    setPreviewTpl(tpl);
    setPreviewLoading(true);
    setPreviewHtml('');
    setValidateResult(null);
    try {
      // 1) Scarica .docx
      const r = await api.get(`/docx-templates/${tpl.id}/download`, { responseType: 'arraybuffer' });
      const result = await mammoth.convertToHtml({ arrayBuffer: r.data });
      // Evidenzia i placeholder con un wrapper
      const html = (result.value || '').replace(/«([^»]+)»/g, '<mark style="background:#fde68a;padding:2px 4px;border-radius:3px;font-family:monospace;font-size:0.85em;">«$1»</mark>');
      setPreviewHtml(html);
      // 2) Valida placeholder
      const v = await api.get(`/docx-templates/${tpl.id}/validate-placeholders`);
      setValidateResult(v.data);
    } catch (e) {
      toast.error('Errore anteprima: ' + (e?.message || ''));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDragStart = (e, placeholder) => {
    e.dataTransfer.setData('text/plain', `«${placeholder}»`);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const copyPlaceholder = (k) => {
    navigator.clipboard.writeText(`«${k}»`).then(() => {
      setCopiedKey(k);
      toast.success(`Copiato «${k}»`);
      setTimeout(() => setCopiedKey(''), 1500);
    });
  };

  return (
    <div className="space-y-6" data-testid="docx-templates-manager-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <FileText className="w-7 h-7 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">Gestione Template Documenti</h1>
            <Badge variant="secondary">{templates.length} template</Badge>
          </div>
          <p className="text-sm text-muted-foreground max-w-3xl mt-2">
            Carica i tuoi modelli Word personalizzati, vedi l'anteprima, verifica i placeholder usati.
            Scarica i <strong>template ufficiali CEN</strong> già pronti, personalizzali in Word
            (logo, intestazione, testo) e ricaricali qui.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} data-testid="upload-template-btn">
          <Upload className="w-4 h-4 mr-2" /> Carica template
        </Button>
      </header>

      {/* Template di esempio scaricabili */}
      <Card className="border-amber-200 bg-amber-50/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />
            Template ufficiali CEN — scarica e personalizza
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {SAMPLE_TEMPLATES.map((s) => {
              const Icon = s.icon;
              return (
                <Button
                  key={s.code}
                  variant="outline"
                  className="justify-start bg-white"
                  onClick={() => downloadSample(s.code)}
                  data-testid={`sample-${s.code}`}
                >
                  <Icon className="w-4 h-4 mr-2 text-amber-700" />
                  <span className="flex-1 text-left">{s.label}</span>
                  <Download className="w-4 h-4 text-muted-foreground" />
                </Button>
              );
            })}
          </div>
          <p className="text-[11px] text-amber-900 mt-3">
            💡 I template ufficiali contengono già tutti i placeholder formattati correttamente
            (es. «titolo_prof.», «Mansione_unilav», tabella DPI con «DPI_ROW»). Aprili in Word,
            modifica logo/intestazione/testo, salva e ricarica qui.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome / descrizione..."
            className="pl-9"
            data-testid="templates-search"
          />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-44" data-testid="templates-filter-cat">
            <SelectValue placeholder="Tutte le categorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            <SelectItem value="sicurezza">Sicurezza (D.Lgs. 81/08)</SelectItem>
            <SelectItem value="formazione">Formazione</SelectItem>
            <SelectItem value="haccp">HACCP</SelectItem>
            <SelectItem value="dpi">DPI</SelectItem>
            <SelectItem value="incarico">Incarichi</SelectItem>
            <SelectItem value="altro">Altro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && <Card><CardContent className="py-8 text-center text-muted-foreground">Caricamento...</CardContent></Card>}

      {!loading && filtered.length === 0 && (
        <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">
          Nessun template trovato. Clicca <strong>Carica template</strong> oppure inizia da uno{' '}
          <strong>ufficiale CEN</strong> qui sopra.
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((tpl) => (
          <Card key={tpl.id} data-testid={`tpl-card-${tpl.id}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-start justify-between gap-2">
                <span className="truncate">{tpl.name}</span>
                <Badge variant="outline" className="capitalize text-[10px]">{tpl.category || 'altro'}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {tpl.description && <p className="text-xs text-muted-foreground">{tpl.description}</p>}
              <div className="text-[11px] text-muted-foreground font-mono truncate">{tpl.original_filename}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="text-[10px]">
                  {(tpl.variables || []).length} placeholder
                </Badge>
              </div>
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <Button size="sm" variant="outline" onClick={() => openPreview(tpl)} data-testid={`tpl-preview-${tpl.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> Anteprima
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDownload(tpl)} data-testid={`tpl-download-${tpl.id}`}>
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Scarica
                </Button>
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => handleDelete(tpl)} data-testid={`tpl-delete-${tpl.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica template Word</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">File .docx *</Label>
              <Input
                type="file"
                accept=".docx"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setUploadFile(f);
                  if (f && !uploadName) setUploadName(f.name.replace(/\.docx$/i, ''));
                }}
                data-testid="upload-file-input"
              />
            </div>
            <div>
              <Label className="text-xs">Nome template *</Label>
              <Input value={uploadName} onChange={(e) => setUploadName(e.target.value)} placeholder="Es. Incarico RSPP CEN 2026" data-testid="upload-name" />
            </div>
            <div>
              <Label className="text-xs">Categoria</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger data-testid="upload-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sicurezza">Sicurezza</SelectItem>
                  <SelectItem value="formazione">Formazione</SelectItem>
                  <SelectItem value="haccp">HACCP</SelectItem>
                  <SelectItem value="dpi">DPI</SelectItem>
                  <SelectItem value="incarico">Incarico</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Descrizione (opzionale)</Label>
              <Input value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} placeholder="Note per gli utenti" data-testid="upload-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Annulla</Button>
            <Button onClick={handleUpload} disabled={uploading} data-testid="upload-confirm">
              {uploading ? 'Caricamento...' : (<><Save className="w-4 h-4 mr-2" />Carica</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview + drag&drop dialog */}
      <Dialog open={!!previewTpl} onOpenChange={(o) => { if (!o) { setPreviewTpl(null); setPreviewHtml(''); } }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> {previewTpl?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
            <div className="flex flex-col overflow-hidden">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="text-sm font-semibold">Anteprima documento</h3>
                {validateResult && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <Badge className="bg-emerald-500 hover:bg-emerald-600 text-[10px]">
                      <Check className="w-3 h-3 mr-1" /> {validateResult.supported.length} placeholder validi
                    </Badge>
                    {validateResult.unknown.length > 0 && (
                      <Badge variant="destructive" className="text-[10px]" title={validateResult.unknown.join(', ')}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> {validateResult.unknown.length} sconosciuti
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <div
                ref={previewRef}
                className="flex-1 overflow-auto border rounded-lg bg-white p-6 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: previewLoading ? '<p class="text-muted-foreground">Caricamento anteprima...</p>' : previewHtml || '<p class="text-muted-foreground">Anteprima non disponibile.</p>' }}
                data-testid="preview-html"
              />
              {validateResult && validateResult.unknown.length > 0 && (
                <div className="mt-2 p-2 border border-red-200 bg-red-50 rounded text-xs text-red-800">
                  <strong>Placeholder non riconosciuti</strong> (non verranno popolati):{' '}
                  {validateResult.unknown.map((u) => <code key={u} className="mr-1">«{u}»</code>)}
                </div>
              )}
            </div>

            <aside className="overflow-auto border rounded-lg bg-muted/30 p-3 space-y-3" data-testid="placeholder-sidebar">
              <div>
                <h3 className="text-sm font-semibold mb-1">Placeholder disponibili</h3>
                <p className="text-[11px] text-muted-foreground">
                  Trascina (drag&amp;drop) nel testo del documento aperto in Word, oppure clicca per
                  copiare e poi incolla.
                </p>
              </div>
              {PLACEHOLDER_GROUPS.map((g) => (
                <div key={g.title}>
                  <div className={`text-[10px] uppercase font-semibold px-2 py-1 rounded ${g.color} mb-1.5`}>{g.title}</div>
                  <div className="flex flex-col gap-1">
                    {g.items.map(([k, desc]) => (
                      <button
                        key={k}
                        type="button"
                        draggable
                        onDragStart={(e) => handleDragStart(e, k)}
                        onClick={() => copyPlaceholder(k)}
                        className="text-left text-xs bg-white border rounded px-2 py-1.5 hover:bg-primary/5 hover:border-primary/30 transition-colors cursor-grab active:cursor-grabbing"
                        data-testid={`drag-placeholder-${k}`}
                        title={`Trascina o clicca per copiare «${k}»`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <code className="font-mono text-[11px] text-primary">«{k}»</code>
                          {copiedKey === k ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-muted-foreground" />}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </aside>
          </div>
          <DialogFooter className="border-t pt-3">
            <p className="text-[11px] text-muted-foreground flex-1">
              💡 Per modificare i contenuti del template clicca <strong>Scarica</strong>,
              aprilo in Word, modifica, salva e <strong>Carica</strong> di nuovo come nuova versione.
            </p>
            <Button variant="outline" onClick={() => previewTpl && handleDownload(previewTpl)}>
              <Download className="w-4 h-4 mr-2" /> Scarica .docx
            </Button>
            <Button onClick={() => setPreviewTpl(null)}><X className="w-4 h-4 mr-2" />Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
